import React from 'react';
import { type PullRequestDetails, type TimelineEvent } from '@/shared/services/supabaseService';
import { Avatar, AvatarFallback, AvatarImage } from '@/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/Card';
import { GitCommit, MessageSquare, FileCheck, Clock, GitMerge, XCircle } from 'lucide-react';
import { format } from 'date-fns';

interface TimelineViewProps {
  pr: PullRequestDetails;
}

const TimelineView: React.FC<TimelineViewProps> = ({ pr }) => {
  const { timeline } = pr;

  const getIcon = (type: TimelineEvent['type']) => {
    switch (type) {
      case 'comment':
        return <MessageSquare className="h-5 w-5 text-gray-500" />;
      case 'review':
        return <FileCheck className="h-5 w-5 text-green-500" />;
      case 'commit':
        return <GitCommit className="h-5 w-5 text-blue-500" />;
      case 'merge':
        return <GitMerge className="h-5 w-5 text-purple-500" />;
      case 'close':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>Timeline</CardTitle>
        <div className="flex space-x-4 text-sm text-muted-foreground mt-2">
            <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                Opened: {new Date(pr.created_at).toLocaleString()}
            </div>
            {pr.merged_at && (
                <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    Merged: {new Date(pr.merged_at).toLocaleString()}
                </div>
            )}
            <div className="flex items-center">
                <FileCheck className={`h-4 w-4 mr-1 ${pr.review ? 'text-green-500' : 'text-gray-400'}`} />
                Codii Review: {pr.review ? 'Completed' : 'Pending'}
            </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <div className="absolute left-5 top-0 h-full w-0.5 bg-border" />
          {timeline.map((event) => (
            <div key={event.id} className="relative flex items-start space-x-4 mb-8">
              {/* Icon and dot container */}
              <div className="absolute left-5 top-5 -translate-y-1/2 flex items-center justify-center">
                <div className="h-4 w-4 bg-background rounded-full border-2 border-border" />
                <div className="absolute">
                  {getIcon(event.type)}
                </div>
              </div>

              {/* Offset content to align with timeline */}
              <div className="flex-1 pl-10">
                <div className="flex items-center space-x-2">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={event.user?.avatar_url} alt={event.user?.name} />
                    <AvatarFallback>{event.user?.name?.charAt(0) || '?'}</AvatarFallback>
                  </Avatar>
                  <span className="font-semibold">{event.user?.name || 'Unknown User'}</span>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(event.created_at), "MMM d, yyyy 'at' h:mm a")}
                  </span>
                </div>
                {event.type === 'comment' || event.type === 'review' ? (
                  <div className="mt-2 p-3 bg-muted rounded-lg">
                    <p className="text-sm">{event.body}</p>
                  </div>
                ) : (
                  <p className="text-sm mt-2">{event.body}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default TimelineView;
